import { bindCallback, EMPTY, from, fromEvent, merge, Observable, of, throwError } from 'rxjs';
import { filter, first, mapTo, mergeMap, race, switchMap, toArray } from 'rxjs/operators';
import { FileHelper } from './file.helper';

export interface DataTransferItemList {
	add(data: File | string, type?: string): DataTransferItem;
	item(index: number): DataTransferItem;
}

function readDirectory(results: any, reader: any, callback: any) {
	reader.readEntries((entries: any) => {
		entries.length === 0
			? callback(results)
			: readDirectory([...results, ...entries], reader, callback);
	});
}

function readFile(fileEntry: any, callback: any) {
	fileEntry.file((file: any) => {
		if (!file.type) {
			Object.defineProperty(file, 'type', {
				value: FileHelper.getMimetypeFromFilename(file.name),
				writable: false,
			});
		}
		if (!file.webkitRelativePath) {
			Object.defineProperty(file, 'fullPath', {
				value: fileEntry.fullPath.replace(/^\/+/, ''),
				writable: false,
			});
		}
		callback(file);
	});
}

function getDirectoryFiles$(entries: any): Observable<any> {
	return from(entries).pipe(mergeMap(getDataTransferItemFiles$));
}

const readFile$ = bindCallback(readFile);
const readDirectory$ = bindCallback(readDirectory);

function getDataTransferItemFiles$(item: any) {
	if (!item) {
		return EMPTY;
	} else if (item.isFile) {
		return readFile$(item);
	} else if (item.isDirectory) {
		return readDirectory$([], item.createReader()).pipe(mergeMap(getDirectoryFiles$));
	} else {
		return throwError(new Error('Invalid item'));
	}
}

export class DragHelper {
	static isDraggingFiles(event: DragEvent): boolean {
		return event.dataTransfer?.types.includes('Files') || false;
	}

	static addData(event: DragEvent, type: string, value: string): void {
		const list: any = event.dataTransfer?.items;
		if (typeof event.dataTransfer?.setData === 'function') {
			event.dataTransfer?.setData(type, value);
		} else if (list && typeof list.add === 'function') {
			list.add(value, type);
		}
	}

	static getData(event: DragEvent, type: string, callback: FunctionStringCallback): void {
		if (typeof event.dataTransfer?.getData === 'function') {
			return callback.call(null, event.dataTransfer?.getData(type) || 'unknown');
		}
		const index: number = event.dataTransfer?.types.indexOf(type) ?? -1;
		const list: any = event.dataTransfer?.items;
		if (index > -1 && list && typeof list.item === 'function') {
			return list.item(index).getAsString(callback);
		}
	}

	static setDragImage(
		event: DragEvent,
		element: HTMLElement,
		effect:
			| 'link'
			| 'none'
			| 'copy'
			| 'copyLink'
			| 'copyMove'
			| 'linkMove'
			| 'move'
			| 'all'
			| 'uninitialized'
	): void {
		if (!event.dataTransfer) {
			return;
		}

		const target = <HTMLElement>event.currentTarget;
		if (!target || !target.style) {
			return;
		}
		event.dataTransfer.effectAllowed = effect;
		if (typeof event.dataTransfer?.setDragImage === 'function') {
			// append custom element to target so that it can be used as the drag image
			target.appendChild(element);
			const x: number = Math.round(element.offsetWidth / 2);
			const y: number = Math.round(element.offsetHeight / 2);
			element.style.position = 'fixed';
			element.style.left = `${event.pageX - x}px`;
			element.style.top = `${event.pageY - y}px`;
			event.dataTransfer.setDragImage(element, x, y);
			requestAnimationFrame(() => target.removeChild(element));
		} else {
			// Edge doesn't support custom drag elements so we add it to the
			// target right before the browser generates the ghost image and
			// then immediately remove it
			const clone: Node = target.cloneNode(true);
			target.parentElement?.replaceChild(clone, target);
			document.body.appendChild(target);
			target.appendChild(element);
			target.classList.add('drag-icon');
			target.style.position = 'fixed';
			target.style.left = Math.round(event.pageX - target.offsetWidth / 2) + 'px';
			target.style.top = Math.round(event.pageY - target.offsetHeight / 2) + 'px';
			requestAnimationFrame(() => {
				target.removeChild(element);
				target.classList.remove('drag-icon');
				target.style.position = target.style.left = target.style.top = '';
				clone.parentElement?.replaceChild(target, clone);
			});
		}
	}

	static getDroppedFiles$(event: DragEvent): Observable<any[]> {
		if (!event.dataTransfer || !event.target) {
			return of([]);
		}

		// get dragged folders
		const directories = Array.from(event.dataTransfer?.items)
			.map(item => item.webkitGetAsEntry?.() ?? null)
			.filter(item => item !== null && item.isDirectory);
		if (directories.length > 1) {
			// no more than 1 parent directory should be uploaded
			return throwError(
				'Please upload only one parent folder (may contain multiple subfolders)'
			);
		} else {
			// concat a list of files by iterating through all dragged files and folders
			const change$: Observable<File[]> = fromEvent(event.target, 'change').pipe(mapTo([]));
			const draggedFiles$: Observable<File> = from(event.dataTransfer.files);
			const directoryFiles$: Observable<File> = <Observable<File>>(
				from(directories).pipe(switchMap(getDataTransferItemFiles$))
			);
			return merge(draggedFiles$, directoryFiles$)
				.pipe(
					filter(file => !!file.type),
					toArray()
				)
				.pipe(race(change$), first());
		}
	}
}
